import { useCustomFilters } from "@features/cx/shared/utils/filters";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { AlertTriangle, ArrowDown, ArrowUp } from "lucide-react";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useComplaintHistoryExport, useComplaintsExport, useHistoricalComplaints } from "../../hooks";
import { Complaint, ComplaintFilters } from "../../types";
import { ComplaintFilterPanel } from "../filters";
// Complaints History List Component
export const HistoryList: React.FC = () => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const renderPriority = (value: string) => {
    if (value === "High") {
      return (
        <span
          style={{
            color: "#e53e3e",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <AlertTriangle size={16} style={{ color: "#e53e3e" }} /> High
        </span>
      );
    }
    if (value === "Medium") {
      return (
        <span
          style={{
            color: "#d97706",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <ArrowUp size={16} style={{ color: "#d97706" }} /> Medium
        </span>
      );
    }
    if (value === "Low") {
      return (
        <span
          style={{
            color: "#2563eb",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <ArrowDown size={16} style={{ color: "#2563eb" }} /> Low
        </span>
      );
    }
    return value;
  };

  const columns: TableColumn<Complaint>[] = [
    { key: "requestNo", header: "Complaint ID", type: "text" },
    {
      key: "utilitySupportRequest",
      header: "Complaint Name",
      render: (value: any) => {
        return `${value.name}`;
      },
    },
    {
      key: "supportRequestType",
      header: "Category/Sub Category",
      render: (value, row) => (
        <div>
          <p className="font-medium">{row.supportRequestType ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.supportRequestSubtype ?? "NA"}
          </p>
        </div>
      ),
    },
    {
      key: "customerName",
      header: "Consumer",
      render: (value, row) => (
        <div>
          <p className="font-medium">{value ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.consumerNo ?? "NA"}
          </p>
        </div>
      ),
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          if (value === "new") return "secondary";
          if (value === "in progress") return "default";
          if (value === "resolved") return "outline";
          if (value === "rejected") return "destructive";
          if (value === "closed") return "outline";
          if (value === "awaiting customer") return "outline";
          if (value === "pending") return "outline";
          if (value === "canceled") return "destructive";
          return "default";
        },
      },
    },
    {
      key: "severityLevel",
      header: "Priority",
      type: "custom",
      render: (value) => renderPriority(value),
    },
    {
      key: "createdDate",
      header: "Created On",
      render: (value, row) => (
        <div>
          <p className="font-medium">{value ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.createdUserRemoteName ?? "NA"}
          </p>
        </div>
      ),
    },
    { key: "slaDueDate", header: "Due Date", type: "text" },
    {
      key: "slaStatus",
      header: "SLA Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          if (value === "within") return "default";
          if (value === "at risk") return "secondary";
          if (value === "breached") return "destructive";
          return "default";
        },
      },
    },
  ];

  // Memoize the configuration to prevent infinite re-renders
  const filterConfig = useMemo(
    () => ({
      paramNames: {
        status: "status",
        category: "category",
        search: "search_data",
        page: "page",
        name: "name",
      },
      defaults: {
        page: 1,
        limit: 50,
        status: [],
        category: "ALL_CATEGORIES",
        search: "",
        name: "name",
      },
      parsers: {
        status: (value: string) => value.split(",").map(Number),
        category: (value: string) => value,
        search: (value: string) => value.trim(),
      },
      serializers: {
        status: (value: number[]) => value.join(","),
        category: (value: string) => value,
        search: (value: string) => value.trim(),
      },
    }),
    []
  );

  const {
    filters,
    page,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleSearch,
  } = useCustomFilters(filterConfig, Number(remoteUtilityId));

  const exportFilters: ComplaintFilters = useMemo(() => {
    return {
      remoteUtilityId: Number(remoteUtilityId),
      searchData: filters.search_data,
      status: filters.status,
      category: filters.category,
      name: filters.name,
      // Note: No page and limit for export to get all data
    };
  }, [filters, remoteUtilityId]);

  const { exportCSV, isExporting } = useComplaintHistoryExport(exportFilters);

  const handleExport = () => {
    exportCSV();
  };

  const { data } = useHistoricalComplaints({
    page: filters.page,
    limit: PAGE_SIZE,
    searchData: filters.search_data,
    status: filters.status,
    category: filters.category,
    remoteUtilityId: Number(remoteUtilityId),
  });

  const actions: TableAction<Complaint>[] = [
    {
      label: "View",
      onClick: (item) => navigate(`/cx/complaints/history/view/${item.id}`),
    },
  ];

  // Transform API data to match the table structure
  const transformedData: Complaint[] =
    data?.results?.map((item: Complaint) => ({
      id: item.id,
      slaSummary: item.slaSummary,
      consumer: item.consumer,
      requestDate: item.requestDate,
      customerName: item.firstName + " " + item.lastName,
      remoteUtilityId: item.remoteUtilityId,
      utilitySupportRequest: item.utilitySupportRequest,
      requestNo: item.requestNo,
      status: item.status,
      statusDisplay: item.statusDisplay,
      requestType: item.requestType,
      createdDate: item.createdDate,
      createdUserRemoteName: item.createdUserRemoteName,
      consumerNo: item.consumerNo,
      firstName: item.firstName,
      lastName: item.lastName,
      address: item.serviceAddress.service?.address,
      serviceAddress: {
        service: {
          hierarchyNames: {
            region: item.serviceAddress.service.hierarchyNames?.region,
            country: item.serviceAddress.service.hierarchyNames?.country,
            state: item.serviceAddress.service.hierarchyNames?.state,
            county: item.serviceAddress.service.hierarchyNames?.county,
            zone: item.serviceAddress.service.hierarchyNames?.zone,
            division: item.serviceAddress.service.hierarchyNames?.division,
            area: item.serviceAddress.service.hierarchyNames?.area,
            subArea: item.serviceAddress.service.hierarchyNames?.subArea,
            premise: item.serviceAddress.service.hierarchyNames?.premise,
          },
          zipcode: item.serviceAddress.service.zipcode,
          premise: item.serviceAddress.service.premise,
          address: item.serviceAddress.service.address,
          unit: item.serviceAddress.service.unit,
        },
        billing: {
          hierarchyNames: {
            region: item.serviceAddress.billing.hierarchyNames?.region,
            country: item.serviceAddress.billing.hierarchyNames?.country,
            state: item.serviceAddress.billing.hierarchyNames?.state,
            county: item.serviceAddress.billing.hierarchyNames?.county,
            zone: item.serviceAddress.billing.hierarchyNames?.zone,
            division: item.serviceAddress.billing.hierarchyNames?.division,
            area: item.serviceAddress.billing.hierarchyNames?.area,
            subArea: item.serviceAddress.billing.hierarchyNames?.subArea,
            premise: item.serviceAddress.billing.hierarchyNames?.premise,
          },
          zipcode: item.serviceAddress.billing.zipcode,
          premise: item.serviceAddress.billing.premise,
          address: item.serviceAddress.billing.address,
          unit: item.serviceAddress.billing.unit,
        },
      },
      utilitySupportName: item.utilitySupportName,
      severityLevel: item.severityLevel,
      slaDueDate: item.slaDueDate,
      timeRemaining: item.timeRemaining,
      slaStatus: item.slaStatus,
      contactNumber: item.consumer.contactNumber,
      email: item.consumer.email,
      category: item.consumer.category,
      subCategory: item.consumer.subCategory,
      supportRequestSubtype: item.supportRequestSubtype,
      supportRequestType: item.supportRequestType,
    })) || [];

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const historyTab = 1;

  return (
    <GenericTable
      data={transformedData}
      columns={columns}
      rowKey="id"
      searchPlaceholder="Search by name, email, or ID..."
      searchParamName="query"
      searchDebounceMs={300}
      onExport={handleExport}
      exportLoading={isExporting}
      onSearch={handleSearch}
      pagination={{
        currentPage: page,
        totalPages,
        totalItems,
        itemsPerPage: PAGE_SIZE,
        startItem: (page - 1) * PAGE_SIZE + 1,
        endItem: Math.min(page * PAGE_SIZE, totalItems),
      }}
      onPageChange={handlePageChange}
      actions={actions}
      actionsType="dropdown"
      filterPanel={
        <ComplaintFilterPanel
          currentFilters={filters}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          tab={historyTab}
        />
      }
    />
  );
};
